import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError } from '../api';
import Icon from '../design/Icon';
import { fmtDateShort, todayStr, addDaysStr } from '../design/atoms';
import type { MemberGym } from '../hooks/useMemberGyms';

/* Check-in history — unified cross-gym view with gym + date-range filters and
   paging (work order B5). gymId omitted = all gyms. */

interface OutletCtx {
  gyms: MemberGym[];
  activeGymId: string | null;
  loading: boolean;
}

interface CheckinLog {
  id: string;
  gym: { id: string; name: string; displayName: string };
  branch: { id: string; name: string; displayName: string } | null;
  checkInTime: string;
  checkOutTime: string | null;
  durationMinutes: number | null;
}

const PAGE_SIZE = 30;

const RANGES = [
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
  { key: '1y', label: 'This year', days: 365 },
  { key: 'all', label: 'All time', days: null as number | null },
];

function isoToYmd(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function MemberCheckinsPage() {
  const { token } = useMemberAuth();
  const { gyms, loading: gymsLoading } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();

  const [gymFilter, setGymFilter] = useState<string | null>(null); // null = all gyms
  const [range, setRange] = useState('90d');
  const [logs, setLogs] = useState<CheckinLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = useMemo(() => {
    const days = RANGES.find(r => r.key === range)?.days;
    return days ? addDaysStr(todayStr(), -days) : undefined;
  }, [range]);

  const fetchPage = useCallback(
    (offset: number) =>
      mfetch(token!, '/member/checkins', {
        params: {
          gymId: gymFilter ?? undefined,
          from,
          limit: String(PAGE_SIZE),
          offset: String(offset),
        },
      }),
    [token, gymFilter, from]
  );

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    fetchPage(0)
      .then(r => {
        setLogs(r.data ?? []);
        setTotal(r.total ?? (r.data ?? []).length);
      })
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load your check-ins'))
      .finally(() => setLoading(false));
  }, [token, fetchPage]);

  useEffect(load, [load]);

  const loadMore = () => {
    if (loadingMore) return;
    setLoadingMore(true);
    fetchPage(logs.length)
      .then(r => {
        setLogs(prev => [...prev, ...(r.data ?? [])]);
        setTotal(r.total ?? total);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  if (!gymsLoading && gyms.length === 0) {
    return (
      <div className="mpage">
        <div className="dp dp-l" style={{ paddingTop: 8 }}>Check-in history</div>
        <div className="card flat"><div className="sub wrap">Join a gym to see your check-ins.</div></div>
      </div>
    );
  }

  return (
    <div className="mpage">
      <div className="row" style={{ paddingTop: 8, gap: 10 }}>
        <button className="icon-btn" aria-label="Back" onClick={() => navigate(-1)}>
          <Icon n="left" s={18} c="var(--ink-2)" />
        </button>
        <div className="col" style={{ gap: 1 }}>
          <div className="dp dp-l">Check-in history</div>
          {!loading && <div className="tiny">{total} visit{total === 1 ? '' : 's'}</div>}
        </div>
      </div>

      {/* Gym filter — only when the member belongs to more than one gym */}
      {gyms.length > 1 && (
        <div className="hscroll">
          <button className={`chip${gymFilter === null ? ' on' : ''}`} onClick={() => setGymFilter(null)}>
            All gyms
          </button>
          {gyms.map(g => (
            <button key={g.id} className={`chip${gymFilter === g.id ? ' on' : ''}`} onClick={() => setGymFilter(g.id)}>
              {g.displayName || g.name}
            </button>
          ))}
        </div>
      )}

      {/* Date-range filter */}
      <div className="hscroll">
        {RANGES.map(r => (
          <button key={r.key} className={`chip${range === r.key ? ' on' : ''}`} onClick={() => setRange(r.key)}>
            {r.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="col" style={{ gap: 9 }}>
          {[58, 58, 58, 58].map((h, i) => <div key={i} className="skel" style={{ height: h }} />)}
        </div>
      ) : error ? (
        <div className="card flat">
          <div className="sub wrap">{error}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
        </div>
      ) : logs.length === 0 ? (
        <div className="card flat" style={{ textAlign: 'center', padding: 26 }}>
          <div className="mid">No check-ins in this window</div>
          <div className="sub wrap" style={{ marginTop: 4 }}>Try a wider date range, or check in to get started.</div>
        </div>
      ) : (
        <>
          <div className="card" style={{ padding: '5px 16px' }}>
            {logs.map(log => (
              <div key={log.id} className="lrow">
                <div className="li">
                  <Icon n="check" s={16} c="var(--ink-2)" />
                </div>
                <div className="col" style={{ gap: 1, minWidth: 0 }}>
                  <div className="mid">
                    {fmtDateShort(isoToYmd(log.checkInTime))} ·{' '}
                    {new Date(log.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div className="tiny trunc">
                    {[
                      gymFilter === null && gyms.length > 1 ? (log.gym.displayName || log.gym.name) : null,
                      log.branch?.displayName,
                    ].filter(Boolean).join(' · ') || log.gym.displayName || log.gym.name}
                  </div>
                </div>
                {log.durationMinutes !== null && (
                  <span className="tag">{log.durationMinutes} min</span>
                )}
              </div>
            ))}
          </div>
          {logs.length < total && (
            <button className="btn ghost block" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : `Load more (${total - logs.length} left)`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
